import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../../components/EditableSection';
import NativeSelect from '../../components/ui/NativeSelect';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdatePriceEntryMutation } from '../../api/account/accountApiSlice';
import type { PriceEntryMode } from '../../api/account/accountApiTypes';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

interface Props {
    readOnly: boolean;
    /** `tenant.config.priceEntry`, as the account serializes it (absent = gross). */
    priceEntry: PriceEntryMode;
    /** Master viewing another tenant: write that tenant by slug. */
    masterSlug?: string;
}

const MODES: PriceEntryMode[] = ['gross', 'net'];

/**
 * Whether this tenant types its prices gross or net (#2967,
 * docs/plans/net-price-entry.md). Only how prices are ENTERED: what is stored,
 * charged and invoiced stays gross either way, so switching changes no price.
 * Hidden for a Kleinunternehmer by the page — without VAT both are the same.
 */
const PriceEntrySection = ({ readOnly, priceEntry, masterSlug }: Readonly<Props>) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updatePriceEntry, { isLoading: saving }] = useUpdatePriceEntryMutation();
    const draft = useEditableDraft<PriceEntryMode>(priceEntry);

    const handleSave = async () => {
        try {
            await updatePriceEntry({ priceEntry: draft.value, slug: masterSlug }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('services.price_entry.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.price_entry.error'), t),
                autoHideDuration: 5000,
            }));
        }
    };

    return (
        <EditableSection
            id={'price-entry'}
            title={t('services.price_entry.section')}
            description={t('services.price_entry.section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            canSave={true}
            onEdit={draft.startEdit}
            onCancel={draft.cancel}
            onSave={() => { void handleSave(); }}
            // From the draft, not the prop: it holds the saved value until the
            // account refetch lands (SeriesSeasonSection, staging 2026-10-13).
            view={<p className={'text-sm text-foreground'}>{t(`services.price_entry.mode.${draft.value}`)}</p>}
        >
            <div className={'flex flex-col gap-2'}>
                <label className={'text-sm font-medium text-foreground'} htmlFor={'price-entry-mode'}>
                    {t('services.price_entry.label')}
                </label>
                <NativeSelect
                    id={'price-entry-mode'}
                    value={draft.value}
                    disabled={saving}
                    onChange={(e) => draft.setDraft(e.target.value === 'net' ? 'net' : 'gross')}
                >
                    {MODES.map((mode) => (
                        <option key={mode} value={mode}>{t(`services.price_entry.mode.${mode}`)}</option>
                    ))}
                </NativeSelect>
                <p className={'text-xs text-foreground/60'}>{t('services.price_entry.hint')}</p>
            </div>
        </EditableSection>
    );
};

export default PriceEntrySection;
